import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "./cityPricing";

const tourrettesSurLoup: ChauffeurPage = {
  slug: "tourrettes-sur-loup",
  title: "Chauffeur privé à Tourrettes-sur-Loup – VTC",
  subtitle:
    "Chauffeur privé et VTC à Tourrettes-sur-Loup : transfert aéroport de Nice, village médiéval, gorges du Loup et cité des violettes.",
  keywords: [
    "chauffeur privé tourrettes-sur-loup",
    "vtc tourrettes-sur-loup",
    "chauffeur tourrettes sur loup",
    "transfert tourrettes aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Tourrettes-sur-Loup",
    subtitle:
      "La cité des violettes, village médiéval perché au-dessus des gorges du Loup : transferts aéroport de Nice et mises à disposition.",
    keywords: [
      "VTC Tourrettes-sur-Loup",
      "chauffeur cité des violettes",
      "transfert aéroport Nice",
      "chauffeur gorges du Loup",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Tourrettes-sur-Loup",
    badges: ["Village médiéval", "Cité des violettes", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Tourrettes-sur-Loup",
    items: [
      {
        title: "Village perché préservé",
        description:
          "Les ruelles médiévales et les remparts se visitent à pied : le chauffeur vous dépose à l’entrée du village et vous reprend sur demande, sans chercher à stationner.",
      },
      {
        title: "Cité des violettes",
        description:
          "Ateliers d’artisans, galeries et fleur emblématique : une journée flâneuse au rythme du village, avec un chauffeur dédié qui patiente.",
      },
      {
        title: "Aéroport de Nice ~40 min",
        description:
          "Rejoignez l’aéroport de Nice Côte d’Azur en environ 40 minutes par Vence et la plaine, avec suivi de vol.",
      },
      {
        title: "Chauffeurs bilingues",
        description:
          "FR/EN, basés sur la Côte d’Azur, ponctuels et discrets — 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Tourrettes-sur-Loup",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Prise en charge à Tourrettes-sur-Loup avec suivi de vol, vers et depuis l’aéroport de Nice Côte d’Azur, en environ 40 minutes.",
      },
      {
        title: "Découverte du village & des galeries",
        text: "Dépose à l’entrée de la cité, attente pendant la visite des ateliers d’artisans et des remparts, reprise sur demande.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée : Vence, Saint-Paul-de-Vence, gorges du Loup et villages perpendiculaires du haut-pays.",
      },
      {
        title: "Escapade gorges du Loup",
        text: "Itinéraire vers Le Bar-sur-Loup, Gourdon et les cascades, avec un chauffeur habitué aux routes sinueuses de l’arrière-pays.",
      },
      {
        title: "Longue distance",
        text: "Trajets toute distance depuis Tourrettes-sur-Loup : Cannes, Monaco, Nice, Italie et stations de ski, en véhicule confortable.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Village médiéval",
        description:
          "Cœur perché, ruelles, remparts et place de la Libération.",
      },
      {
        name: "Ateliers d’artisans & galeries",
        description:
          "Souffleurs de verre, peintres et artisans de la cité des violettes.",
      },
      {
        name: "Gorges du Loup",
        description: "Vallée sauvage, cascades et route vers Gourdon.",
      },
      {
        name: "Vence",
        description: "Ville voisine, chapelle Matisse et cœur historique.",
      },
      {
        name: "Saint-Paul-de-Vence",
        description: "Village d’art tout proche, fondation Maeght.",
      },
      {
        name: "Aéroport de Nice",
        description: "À environ 40 minutes par Vence et la plaine.",
      },
    ],
  },
  generic: {
    heading: "Tourrettes-sur-Loup, la cité des violettes",
    subheading:
      "Un village médiéval perché au-dessus des gorges du Loup, entre artisanat et haut-pays.",
    paragraphs: [
      "Surnommée la cité des violettes, Tourrettes-sur-Loup aligne ses maisons en rempart au-dessus des gorges du Loup. Ses ruelles médiévales, ses ateliers d’artisans et ses galeries se découvrent à pied ; le chauffeur vous dépose à l’entrée du village et patiente le temps de la flânerie, sans contrainte de stationnement.",
      "Le village est aussi une porte du haut-pays : Vence, Saint-Paul-de-Vence, Le Bar-sur-Loup, Gourdon et les cascades des gorges du Loup sont à portée de route. Nos chauffeurs, à l’aise sur les routes sinueuses de l’arrière-pays, composent l’itinéraire selon vos envies.",
      "Pour l’aéroport de Nice Côte d’Azur, comptez environ 40 minutes par Vence et la plaine selon le trafic ; nos chauffeurs bilingues suivent votre vol et adaptent le départ en conséquence.",
    ],
    bullets: [
      "Dépose à l’entrée du village",
      "Escapades dans le haut-pays",
      "Suivi de vol & devis immédiat",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Tourrettes-sur-Loup",
    steps: [
      "Indiquez votre trajet (village, gorges du Loup, aéroport de Nice) et vos horaires.",
      "Nous confirmons le véhicule adapté et le forfait fixe ou la mise à disposition.",
      "Votre chauffeur suit votre vol et vous accueille à l’adresse convenue.",
      "Ajustements en direct selon le trafic et vos envies de découverte.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Tourrettes-sur-Loup"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice ?",
      a: "Comptez environ 40 minutes par Vence et la plaine selon le trafic, avec suivi de vol et accueil personnalisé.",
    },
    {
      q: "Peut-on visiter le village et les gorges du Loup avec chauffeur ?",
      a: "Oui, nous vous déposons à l’entrée de la cité et composons une escapade dans les gorges du Loup (Le Bar-sur-Loup, Gourdon, cascades), avec attente pendant vos visites.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et lors de la fête des violettes ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Desservez-vous Vence et Saint-Paul-de-Vence ?",
      a: "Oui, les villages voisins de Vence et Saint-Paul-de-Vence sont à quelques minutes ; nous assurons transferts et mises à disposition dans tout le secteur.",
    },
  ],
  relatedLinks: [
    {
      label: "Service de chauffeur sur la Riviera",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Aéroport de Nice, tarifs et trajets",
      href: "/chauffeur/aeroport",
    },
    { label: "Service de chauffeur à Nice", href: "/chauffeur/nice" },
    {
      label: "Louer une voiture Vence",
      href: "/location/location-voiture-vence",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Tourrettes-sur-Loup",
    text: "Transfert aéroport de Nice, découverte du village ou mise à disposition — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé Tourrettes-sur-Loup — VTC & transfert",
    description:
      "Chauffeur privé et VTC à Tourrettes-sur-Loup : transfert aéroport de Nice en ~40 min, village médiéval, gorges du Loup. 5,0/5 sur 38 avis. Devis immédiat.",
    canonical: "/chauffeur/tourrettes-sur-loup",
  },
};

export default tourrettesSurLoup;
